import { useState, useMemo, forwardRef, useImperativeHandle, useEffect } from 'react'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuGroup, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, Kbd, cn } from '@duck/ui'
import { Plus, Columns2 } from 'lucide-react'
import { workspaceTabsDefinitions, workspaceTabsOrder } from './workspaceTabs.definitions'
import { ChallengeService } from '@duck/api-definitions'
import { getTabClassName } from './WorkspaceTab'
import { useTabStyle } from './tabStyle'
import { useTar } from '@/modules/i18n'
import type { TranslationKey } from '@/modules/i18n'
import { useAtomValue } from 'jotai'
import { workspaceRecentTipsAtom, workspaceChallengeIdAtom } from '@/store/workspaceAtoms'
import { trackWorkspace } from '@/modules/workspaceAnalytics'
import { useAvailableWorkspaceTabs } from '@/hooks/useAvailableWorkspaceTabs'
import { ShortcutHint } from '@/modules/Shortcuts'

export interface AddWorkspaceTabTabRef {
    open: () => void
}

export interface AddWorkspaceTabTabProps {
    onAddTab: (tabType: ChallengeService.WorkspaceTabTypeEnum) => void
    onOpenInSplitView?: (tabType: ChallengeService.WorkspaceTabTypeEnum) => void
    /** Tooltip for the split/other-view button: e.g. 'common.openInSplitView' or 'common.openInOtherView' */
    openInSplitViewTooltipKey?: TranslationKey
    /** Set of tab types currently open across all views */
    openTabTypes?: Set<ChallengeService.WorkspaceTabTypeEnum>
    /** When true, render the Alt+'+' ShortcutHint over the trigger while Alt is held. */
    showShortcutHint?: boolean
    className?: string
}

export const AddWorkspaceTabTab = forwardRef<AddWorkspaceTabTabRef, AddWorkspaceTabTabProps>(({ onAddTab, onOpenInSplitView, openInSplitViewTooltipKey = 'common.openInSplitView', openTabTypes, showShortcutHint, className }, ref) => {
    const { t, isRtl } = useTar()
    const [isOpen, setIsOpen] = useState(false)
    const recentTips = useAtomValue(workspaceRecentTipsAtom)
    const challengeId = useAtomValue(workspaceChallengeIdAtom)
    const availableTabs = useAvailableWorkspaceTabs()
    const tabStyle = useTabStyle()
    const visibleTabsOrder = useMemo(
        () => workspaceTabsOrder.filter((type) => {
            if (!availableTabs.has(type)) return false
            if (type === ChallengeService.WorkspaceTabTypeEnum.TipsHistory) return recentTips.length > 0
            return true
        }),
        [recentTips, availableTabs]
    )

    useImperativeHandle(ref, () => ({
        open: () => setIsOpen(true),
    }), [])

    // While open, map digits 1..9 to the corresponding visible item. Capture phase so Radix's
    // built-in typeahead doesn't consume the keystroke first.
    useEffect(() => {
        if (!isOpen) return
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key < '1' || e.key > '9') return
            if (e.altKey || e.metaKey || e.ctrlKey) return
            const index = parseInt(e.key, 10) - 1
            const tabType = visibleTabsOrder[index]
            if (!tabType) return
            const def = workspaceTabsDefinitions[tabType]
            const isOnlyOneAndOpen = !!def?.onlyOne && !!openTabTypes?.has(tabType)
            if (isOnlyOneAndOpen) return
            e.preventDefault()
            e.stopPropagation()
            onAddTab(tabType)
            trackWorkspace('workspace:tab:add', { challengeId, tabType, method: 'keyboard' })
            setIsOpen(false)
        }
        document.addEventListener('keydown', onKeyDown, true)
        return () => document.removeEventListener('keydown', onKeyDown, true)
    }, [isOpen, visibleTabsOrder, openTabTypes, onAddTab, challengeId])

    const dropdown = (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <div
                    role="button"
                    tabIndex={0}
                    className={getTabClassName(false, false, true, className, tabStyle)}
                    style={{ zIndex: 1 }}
                >
                    <span className="relative z-10 pt-1">
                        <Plus className="size-4" />
                    </span>
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-48">
                <DropdownMenuGroup>
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('common.newTab')}</DropdownMenuLabel>
                    {visibleTabsOrder.map((tabType, index) => {
                        const def = workspaceTabsDefinitions[tabType]
                        const DefIcon = def.Icon
                        const isOnlyOneAndOpen = !!def.onlyOne && !!openTabTypes?.has(tabType)
                        const hintNumber = index + 1

                        if (isOnlyOneAndOpen) {
                            return (
                                <TooltipProvider key={tabType}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="group flex items-center w-full">
                                                <DropdownMenuItem
                                                    disabled
                                                    className="flex-1 cursor-not-allowed opacity-50 gap-2"
                                                >
                                                    <DefIcon className={cn('size-4 shrink-0 me-1', def.iconColor)} aria-hidden />
                                                    {t(def.label)}
                                                </DropdownMenuItem>
                                                <div className="relative flex items-center justify-center w-9 h-8 shrink-0">
                                                    <Kbd>{hintNumber}</Kbd>
                                                </div>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {t('common.onlyOneTabOpen', { tabName: t(def.label) })}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )
                        }

                        return (
                            <div key={tabType} className="group flex items-center">
                                <DropdownMenuItem
                                    onClick={() => {
                                        onAddTab(tabType)
                                        trackWorkspace('workspace:tab:add', { challengeId, tabType })
                                    }}
                                    className="flex-1 cursor-pointer gap-2 hover:bg-transparent focus:bg-transparent hover:text-primary focus:text-primary"
                                >
                                    <DefIcon className={cn('size-4 shrink-0 me-1', def.iconColor)} aria-hidden />
                                    {t(def.label)}
                                </DropdownMenuItem>
                                <div className="relative flex items-center justify-center w-9 h-8 shrink-0">
                                    <span className="transition-opacity group-hover:opacity-0">
                                        <Kbd>{hintNumber}</Kbd>
                                    </span>
                                    {onOpenInSplitView && (
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div
                                                        role="button"
                                                        tabIndex={0}
                                                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity hover:text-primary focus:text-primary cursor-pointer"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            onOpenInSplitView(tabType)
                                                            trackWorkspace('workspace:split:open', { challengeId, tabType })
                                                            setIsOpen(false)
                                                        }}
                                                    >
                                                        <Columns2 className="size-4" />
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent side={isRtl ? 'left' : 'right'}>
                                                    {t(openInSplitViewTooltipKey)}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )

    return showShortcutHint
        ? <ShortcutHint shortcutId="workspace.tabs.addTab" hideModifiers>{dropdown}</ShortcutHint>
        : dropdown
})
AddWorkspaceTabTab.displayName = 'AddWorkspaceTabTab'
